<template>
  <div class="app-container">
    <header class="app-header">
      <nav class="main-nav">
        <div class="nav-top">
          <router-link to="/" class="logo">
            <img src="./assets/images/logo.svg" alt="小乌龟语言学习" class="logo-image">
            <span class="logo-text">小乌龟语言学习</span>
          </router-link>
          <button class="menu-toggle" @click="toggleMenu" aria-label="切换菜单">
            <span class="bar"></span>
            <span class="bar"></span>
            <span class="bar"></span>
          </button>
        </div>
        <div class="nav-content" :class="{ 'nav-open': menuOpen }">
          <div class="nav-links" v-if="isLoggedIn">
            <router-link to="/import" v-if="isAdmin">导入学习内容</router-link>
            <router-link to="/practice">闪卡练习</router-link>
            <router-link to="/review">错题复习</router-link>
            <router-link to="/users" v-if="isAdmin">用户管理</router-link>
          </div>
          <div class="user-menu" v-if="isLoggedIn">
            <span class="username">{{ currentUser ? currentUser.username : '' }}</span>
            <span class="user-role" v-if="currentUser && currentUser.role">{{ currentUser.role === 'admin' ? '(管理员)' : '(用户)' }}</span>
            <button class="logout-btn" @click="logout">登出</button>
          </div>
          <div class="nav-links" v-else>
            <router-link to="/login" class="login-btn">登录</router-link>
          </div>
        </div>
      </nav>
    </header>
    
    <main class="app-main">
      <router-view />
    </main>
    
    <footer class="app-footer">
      <p>© 2025 小乌龟语言学习 - 帮助儿童和青少年学习语言的好伙伴</p>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { authService } from './services/auth';

const router = useRouter();
const route = useRoute();
const currentUser = ref(null);
const menuOpen = ref(false);

// 计算属性
const isLoggedIn = computed(() => {
  return authService.isLoggedIn();
});

const isAdmin = computed(() => {
  return authService.isAdmin();
});

// 生命周期钩子
onMounted(async () => {
  // 确保在应用加载时立即检查登录状态
  await checkLoginStatus();
  
  // 添加全局事件监听，用于在登录/登出时立即更新状态
  window.addEventListener('login-state-changed', async () => {
    await checkLoginStatus();
  });
  
  // 添加点击外部区域关闭菜单的事件监听
  document.addEventListener('click', (event) => {
    const navContent = document.querySelector('.nav-content');
    const menuToggle = document.querySelector('.menu-toggle');
    
    if (menuOpen.value && 
        navContent && 
        !navContent.contains(event.target) && 
        menuToggle && 
        !menuToggle.contains(event.target)) {
      menuOpen.value = false;
    }
  });
});

// 监听路由变化，更新用户信息并关闭移动端菜单
watch(() => route.path, async () => {
  await checkLoginStatus();
  // 路由变化时关闭菜单
  menuOpen.value = false;
});

// 监听登录状态变化
watch(isLoggedIn, async (newValue) => {
  // 无论是登录还是登出，都重新检查用户状态
  await checkLoginStatus();
});

// 方法
async function checkLoginStatus() {
  try {
    if (authService.isLoggedIn()) {
      // 强制从服务器获取最新用户信息
      currentUser.value = await authService.getCurrentUser();
      
      // 强制更新计算属性
      isLoggedIn.value;
      isAdmin.value;
      
      // 确保DOM更新
      await nextTick();
    } else {
      // 确保在未登录状态下清除用户信息
      currentUser.value = null;
    }
  } catch (error) {
    console.error('获取用户信息失败:', error);
  }
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

async function logout() {
  // 先清除认证服务中的数据
  authService.logout();
  
  // 重置本地状态
  currentUser.value = null;
  
  // 触发自定义事件，通知应用登录状态已改变
  window.dispatchEvent(new Event('login-state-changed'));
  
  // 确保路由跳转前所有状态都被清除
  // 强制刷新路由状态
  await router.push('/login');
  
  // 强制刷新页面以确保所有状态都被重置
  window.location.reload();
}
</script>

<style>
/* 全局样式将在main.css中定义 */
.nav-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 21px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
}

.bar {
  height: 3px;
  width: 100%;
  background-color: var(--primary-color);
  border-radius: 10px;
  transition: var(--transition);
}

.nav-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.user-menu {
  display: flex;
  align-items: center;
  margin-left: 1rem;
}

.username {
  margin-right: 0.5rem;
  font-weight: bold;
  color: var(--primary-color);
}

.user-role {
  margin-right: 0.5rem;
  font-size: 0.8rem;
  color: #666;
}

.logout-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.25rem 0.5rem;
  border-radius: var(--border-radius);
  transition: var(--transition);
}

.logout-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.change-password-link {
  margin-right: 0.75rem;
  color: var(--primary-color);
  font-size: 0.9rem;
  transition: var(--transition);
}

.change-password-link:hover {
  color: var(--secondary-color);
  text-decoration: underline;
}

.login-btn {
  padding: 0.25rem 0.75rem;
  background-color: var(--primary-color);
  color: white;
  border-radius: var(--border-radius);
  transition: var(--transition);
}

.login-btn:hover {
  background-color: var(--secondary-color);
  transform: translateY(-2px);
}

/* 移动端响应式样式 */
@media (max-width: 768px) {
  /* 菜单切换按钮样式优化 */
  .menu-toggle {
    display: flex;
    margin-right: 5px;
  }
  
  .bar {
    height: 2px;
    margin: 2px 0;
  }
  
  /* 导航内容容器样式优化 */
  .nav-content {
    flex-direction: column;
    position: absolute;
    top: 60px; /* 减小顶部间距 */
    left: 0;
    width: 100%;
    background: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 100;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-out, padding 0.3s ease;
    padding: 0;
  }
  
  .nav-content.nav-open {
    max-height: 500px;
    padding: 0.5rem 0; /* 减小上下内边距 */
  }
  
  /* 导航链接样式优化 */
  .nav-links {
    flex-direction: column;
    width: 100%;
    gap: 0;
  }
  
  .nav-links a {
    display: block;
    padding: 12px 16px; /* 增大点击区域 */
    width: 100%;
    text-align: center;
    border-radius: 0;
  }
  
  /* 用户菜单样式优化 */
  .user-menu {
    flex-direction: column;
    align-items: center;
    margin: 10px 0;
    width: 100%;
    padding: 10px 0;
    border-top: 1px solid #eee;
  }
  
  .username, .user-role {
    margin-right: 0;
    margin-bottom: 5px;
  }
  
  .logout-btn {
    width: 80%;
    padding: 8px 0;
    margin-top: 5px;
    text-align: center;
    background-color: #f5f5f5;
  }
  
  /* 登录按钮样式优化 */
  .nav-links .login-btn {
    margin: 10px auto;
    width: 80%;
    text-align: center;
  }
}
</style>